// Power/element tile used in Match, Save, and Grow. One shape, three uses:
//   - Match: the whole tile cycles the four weight levels; the artwork's
//            sharpness mirrors the level (blurred = Nicht relevant, sharp =
//            Hoch). See @adr SPARK-0033-power-weights-word-scale-plus-exact-field@1 and spark #173.
//   - Save:  measured Level 0..100, scoreSuffix='%' , read-only
//   - Grow:  measured Level 0..100 (or '-'); onSelect navigates to detail
// Visual primitives `.power-tile` / `.power-tile--active` live in index.css.

import { useState } from "react";
import { useIntl } from "react-intl";

type Variant = "default" | "score";

// Weight mode state display: the artwork sharpens and the scrim lifts as the
// level rises, so relevance is readable at a glance without any control
// chrome. Index = level.
const WEIGHT_IMAGE_BLUR = ["blur-md", "blur-[6px]", "blur-[3px]", "blur-none"] as const;
const WEIGHT_IMAGE_SCRIM = ["bg-page/80", "bg-page/60", "bg-page/40", "bg-page/15"] as const;
// Blurred images grow slightly so the soft blur edge cannot bleed a light
// seam into the tile (same trick as discovery_app's GrowElementCard).
const WEIGHT_IMAGE_SCALE = ["scale-110", "scale-110", "scale-105", "scale-100"] as const;
// discovery_app's card gradient: dense top and bottom text zones, nearly
// free middle so the artwork carries the card. Stops mirror
// GrowElementCard.swift (0.78 / 0.08@30% / 0.04@55% / 0.86).
const WEIGHT_TEXT_GRADIENT =
  "linear-gradient(to bottom, color-mix(in srgb, var(--color-page) 78%, transparent) 0%, color-mix(in srgb, var(--color-page) 8%, transparent) 30%, color-mix(in srgb, var(--color-page) 4%, transparent) 55%, color-mix(in srgb, var(--color-page) 86%, transparent) 100%)";

const ROLODEX_ROW_PX = 19;
// Soft clip at both edges so rolling words fade in/out instead of hard-cutting.
const ROLODEX_MASK =
  "linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)";
// Soft page-colored halo instead of a chip box, so the level word stays
// readable on the artwork without covering it.
const ROLODEX_HALO =
  "0 1px 2px var(--color-page), 0 0 6px var(--color-page), 0 0 12px var(--color-page)";

// Rolodex chip: the level words sit on a vertical roller ordered so that
// higher levels sit higher up; only the current word is visible. A level
// change rolls the next card down into place; the wrap from Hoch back to
// Nicht relevant rolls onto a duplicated Nicht relevant at the very top and
// then snaps back without transition, so the roller only ever turns forward.
function WeightLevelRolodex({
  level,
  labels,
}: {
  level: 0 | 1 | 2 | 3;
  labels: readonly [string, string, string, string];
}) {
  // Top-down: duplicated Nicht relevant, Hoch, Mittel, Niedrig, Nicht relevant.
  const rows = [labels[0], labels[3], labels[2], labels[1], labels[0]];
  const rowFor = (l: 0 | 1 | 2 | 3) => rows.length - 1 - l;
  const [prev, setPrev] = useState<0 | 1 | 2 | 3>(level);
  const [visual, setVisual] = useState<number>(rowFor(level));
  const [withTransition, setWithTransition] = useState(false);
  // Adjust-state-during-render pattern: reacting to the level prop without an
  // effect, so the roll starts in the same commit as the level change.
  if (prev !== level) {
    setPrev(level);
    setWithTransition(true);
    setVisual(prev === 3 && level === 0 ? 0 : rowFor(level));
  }
  return (
    <span
      className="block overflow-hidden"
      style={{
        height: ROLODEX_ROW_PX,
        maskImage: ROLODEX_MASK,
        WebkitMaskImage: ROLODEX_MASK,
      }}
    >
      <span
        className={`flex flex-col items-center ${withTransition ? "transition-transform duration-300 ease-out" : ""}`}
        style={{ transform: `translateY(${-visual * ROLODEX_ROW_PX}px)` }}
        onTransitionEnd={() => {
          if (visual === 0) {
            setWithTransition(false);
            setVisual(rows.length - 1);
          }
        }}
      >
        {rows.map((label, i) => (
          <span
            key={i}
            className={`flex items-center justify-center text-smycles-body leading-none ${
              i === visual ? "font-medium text-ink" : "text-ink-muted"
            }`}
            style={{ height: ROLODEX_ROW_PX }}
          >
            {label}
          </span>
        ))}
      </span>
    </span>
  );
}

interface PowerTileProps {
  tag?: string;
  abbr: string;
  name: string;
  score?: number | string | null;
  scoreSuffix?: string;
  active?: boolean;
  badge?: boolean;
  onSelect?: () => void;
  selectTitle?: string;
  // When set, the power artwork fades in (blurred → sharp) on hover. Only
  // passed once the user has rated the power, so it doubles as an unlock.
  imageSrc?: string | null;
  // Discrete weight mode (Match team editor): the whole tile becomes the
  // control. A click cycles to the next level (the tile calls
  // onWeightLevelSelect with level + 1, wrapping to 0 after Hoch) and the
  // artwork blur/scrim mirror the current level. Omit onWeightLevelSelect
  // for a read-only display.
  weightLevel?: 0 | 1 | 2 | 3 | null;
  onWeightLevelSelect?: (level: 0 | 1 | 2 | 3) => void;
  // Labels per level (index = level), drives title/aria and the level word
  // chip in the top-right corner.
  weightLevelLabels?: readonly [string, string, string, string];
  // Permanent artwork without the weight controls (roleplay power choice): "sharp" is the chosen state, "blurred"
  // the open one, in the weight mode's top step and its second-highest one, so an open Power stays recognisable instead
  // of fading to near white (spark#1236). Works with onSelect.
  artwork?: "sharp" | "blurred";
  // A tile that cannot be chosen right now: off the tab order, its words and artwork faded to the disabled ink, no
  // coloured border (a state is a neutral signal, never the accent or danger colour). Works with onSelect. A pressed
  // tile keeps its chosen look while disabled: it is still chosen, only the tap is gone, so the fade is for tiles that
  // cannot be chosen.
  disabled?: boolean;
  // A change is on its way: the tile ignores taps and says so (aria-disabled) but keeps its focus and its look, so a
  // keyboard or screen reader user stays on the tile they tapped. Works with onSelect.
  busy?: boolean;
  // Whether onSelect's choice is on (aria-pressed), for a tile that is a toggle (roleplay power choice). Omit it for a
  // tile that navigates or only shows.
  pressed?: boolean;
  variant?: Variant;
  className?: string;
  // Testanker; sitzt auf dem bedienbaren Element, damit eine Kachel genau
  // einmal zählt (docs/UI_CONVENTIONS.md → „Test anchors").
  id?: string;
}


export function PowerTile({
  tag,
  abbr,
  name,
  score = null,
  scoreSuffix = "",
  active = true,
  badge = false,
  onSelect,
  selectTitle,
  imageSrc = null,
  weightLevel = null,
  onWeightLevelSelect,
  weightLevelLabels,
  artwork,
  disabled = false,
  busy = false,
  pressed,
  variant = "default",
  className = "",
  id,
}: PowerTileProps) {
  const intl = useIntl();
  const interactive = Boolean(onSelect);
  const artLevel = weightLevel ?? (artwork === "sharp" ? 3 : artwork === "blurred" ? 2 : null);

  // Weight mode: the artwork is a permanent state display, sharpness =
  // relevance. A constant bottom gradient keeps name/abbr legible even on
  // the sharp Hoch level.
  const weightArtwork =
    artLevel != null && imageSrc ? (
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden>
        <img
          src={imageSrc}
          alt=""
          className={`absolute inset-0 h-full w-full object-cover transition-all duration-300 ${WEIGHT_IMAGE_BLUR[artLevel]} ${WEIGHT_IMAGE_SCALE[artLevel]}`}
        />
        <div
          className={`absolute inset-0 transition-colors duration-300 ${WEIGHT_IMAGE_SCRIM[artLevel]}`}
        />
        <div className="absolute inset-0" style={{ background: WEIGHT_TEXT_GRADIENT }} />
      </div>
    ) : null;

  // Hover reveal: artwork sits behind the tile content, hidden + blurred until
  // hover. A fixed white scrim keeps the artwork independent of the page colour.
  // Not used in weight mode, where the artwork encodes the level instead.
  const reveal = artLevel == null && imageSrc ? (
    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden>
      <img
        src={imageSrc}
        alt=""
        className="absolute inset-0 h-full w-full scale-110 object-cover opacity-0 blur-md transition-all duration-500 ease-out group-hover:scale-100 group-hover:opacity-100 group-hover:blur-none"
      />
      <div className="absolute inset-0 bg-white/55 opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100" />
    </div>
  ) : null;

  // variant='score' (Save profile): no category tag, abbreviation moves to
  // the top-left, score sits center-right as hero, read-only.
  if (variant === "score") {
    const bodyContent = (
      <>
        <span className="flex items-start justify-between gap-2">
          <span className="font-serif text-smycles-title leading-none text-ink">{abbr}</span>
          {score != null ? (
            <span id={id && `${id}-score`} className="shrink-0 text-smycles-body font-medium leading-none tabular-nums text-ink">
              {score}
            </span>
          ) : null}
        </span>
        <span lang="de" className="block min-w-0 hyphens-auto text-smycles-micro tracking-[0.12em] leading-tight text-ink line-clamp-2">
          {name}
        </span>
      </>
    );

    return (
      <div
        className={`power-tile ${active ? "power-tile--active" : ""} relative aspect-square min-h-0 ${className}`}
        title={name}
      >
        {interactive ? (
          <button
            id={id}
            type="button"
            onClick={onSelect}
            title={selectTitle}
            className="absolute inset-0 flex w-full min-w-0 cursor-pointer flex-col justify-between p-2 text-left"
          >
            {bodyContent}
          </button>
        ) : (
          <div id={id} className="absolute inset-0 flex w-full min-w-0 flex-col justify-between p-2 text-left">
            {bodyContent}
          </div>
        )}
      </div>
    );
  }

  const bodyContent = (
    <>
      <span className="flex items-center gap-1 text-smycles-label leading-none tracking-widest text-ink-muted">
        {tag}
        {badge ? (
          <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-label={intl.formatMessage({ id: "design.selfAssessmentDone", defaultMessage: "Self-assessment completed" })}
            className="stroke-accent-decoration"
          >
            <circle cx="12" cy="8" r="6" />
            <path d="M8.5 13.5 7 22l5-3 5 3-1.5-8.5" />
          </svg>
        ) : null}
      </span>
      {/* Name-first hierarchy: the readable power name is the hero, the
          three-letter code drops to a small footnote (#173). */}
      <span className="block min-w-0">
        <span
          lang="de"
          className={`block min-w-0 font-serif text-smycles-body leading-tight hyphens-auto line-clamp-2 ${active ? "" : "text-ink"}`}
        >
          {name}
        </span>
        {/* Over a photo (revealed on hover) muted text has no computed contrast; full ink does. */}
        <span className={`mt-1 block text-smycles-micro tracking-[0.12em] leading-none ${imageSrc ? "text-ink" : "text-ink-muted"}`}>
          {abbr}
        </span>
      </span>
    </>
  );

  // Weight-mode body (#173): no group tag; the power's own code sits small
  // with the full name as the larger line below, anchored to the card's
  // bottom zone. The relevance roller owns the top zone.
  const weightBody = (
    <span className="block min-w-0">
      <span className="block text-smycles-micro tracking-[0.12em] leading-none text-ink">{abbr}</span>
      <span
        lang="de"
        className="mt-1 block min-w-0 font-serif text-smycles-title leading-tight hyphens-auto line-clamp-2"
      >
        {name}
      </span>
    </span>
  );

  return (
    <div
      className={`power-tile group ${reveal ? "hover:[--context-ink:black] hover:[--context-ink-muted:black] hover:text-ink" : ""} ${active ? "power-tile--active" : ""} ${interactive && disabled && !pressed ? "power-tile--unavailable" : ""} relative aspect-square min-h-0 ${className}`}
      title={name}
    >
      {weightArtwork}
      {reveal}
      {weightLevel != null && onWeightLevelSelect ? (
        <button
          id={id}
          type="button"
          onClick={() => onWeightLevelSelect(((weightLevel + 1) % 4) as 0 | 1 | 2 | 3)}
          title={
            weightLevelLabels
              ? `${weightLevelLabels[weightLevel]} → ${weightLevelLabels[((weightLevel + 1) % 4) as 0 | 1 | 2 | 3]}`
              : undefined
          }
          aria-label={
            weightLevelLabels ? `${name}: ${weightLevelLabels[weightLevel]}` : name
          }
          className="absolute inset-0 z-10 flex w-full min-w-0 cursor-pointer flex-col justify-end p-2 text-left"
        >
          {weightBody}
        </button>
      ) : interactive ? (
        <button
          id={id}
          type="button"
          onClick={busy ? undefined : onSelect}
          title={selectTitle}
          disabled={disabled}
          aria-disabled={busy && !disabled ? true : undefined}
          aria-pressed={pressed}
          className="absolute inset-0 z-10 flex w-full min-w-0 cursor-pointer flex-col justify-between p-2 text-left disabled:cursor-not-allowed aria-disabled:cursor-progress"
        >
          {bodyContent}
        </button>
      ) : (
        <div
          id={id}
          className={`absolute inset-0 z-10 flex w-full min-w-0 flex-col p-2 text-left ${
            weightLevel != null ? "justify-end" : "justify-between"
          }`}
        >
          {weightLevel != null ? weightBody : bodyContent}
        </div>
      )}

      {weightLevel != null && weightLevelLabels ? (
        // aria-hidden: the state is announced via the cycle button's label,
        // the roller is the visual twin. Sits in the card's top zone (the
        // dense end of the gradient) with name/abbr anchored below; the halo
        // keeps it readable without a chip box.
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-2 top-3 z-20 flex justify-center"
          style={{ textShadow: ROLODEX_HALO }}
        >
          <WeightLevelRolodex level={weightLevel} labels={weightLevelLabels} />
        </span>
      ) : score != null ? (
        <span className="pointer-events-none absolute right-2 top-2 z-20 text-smycles-meta font-medium leading-none tabular-nums text-ink">
          {score}
          {scoreSuffix}
        </span>
      ) : null}
    </div>
  );
}
